import React, { useState, useRef, useCallback, useEffect } from 'react';
import { IoIosMusicalNotes } from 'react-icons/io';
import {
  Upload, Play, Pause, SkipBack, Plus, Trash2, Wand2,
  ImagePlus, RefreshCw, ChevronLeft, Download,
} from 'lucide-react';
import { generateMusic } from '../endpoints/music-video/generate-music_POST.schema';
import { generateSceneImage } from '../endpoints/music-video/generate-scene-image_POST.schema';
import { uploadAudio } from '../endpoints/music-video/upload-audio_POST.schema';
import css from './MusicVideoEditor.module.css';

// ── Types ──

interface LyricLine {
  id: string;
  time: number; // seconds
  text: string;
}

interface Scene {
  id: string;
  imageUrl: string | null;
  prompt: string;
  startTime: number; // seconds
  duration: number; // seconds
  kenBurns: 'zoomIn' | 'zoomOut' | 'panRight' | 'panLeft';
}

interface MusicVideoProject {
  title: string;
  audioUrl: string | null;
  audioDuration: number;
  lyrics: LyricLine[];
  scenes: Scene[];
  musicPrompt: string;
  lyricsText: string;
}

const KEN_BURNS_OPTIONS = [
  { value: 'zoomIn', label: 'Zoom In' },
  { value: 'zoomOut', label: 'Zoom Out' },
  { value: 'panRight', label: 'Pan Right' },
  { value: 'panLeft', label: 'Pan Left' },
] as const;

const KEN_BURNS_CLASS: Record<string, string> = {
  zoomIn: css.kenBurnsZoomIn,
  zoomOut: css.kenBurnsZoomOut,
  panRight: css.kenBurnsPanRight,
  panLeft: css.kenBurnsPanLeft,
};

const SCENE_COLORS = [
  '#e74c3c', '#3498db', '#2ecc71', '#f39c12',
  '#9b59b6', '#1abc9c', '#e67e22', '#2980b9',
];

function uid() {
  return Math.random().toString(36).slice(2, 10);
}

function fmtTime(s: number): string {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

function parseLyrics(text: string, totalDuration: number): LyricLine[] {
  const lines = text.split('\n').filter((l) => l.trim());
  if (lines.length === 0) return [];
  const interval = totalDuration / lines.length;
  return lines.map((line, i) => ({
    id: uid(),
    time: i * interval,
    text: line.trim(),
  }));
}

// ── Component ──

export function MusicVideoEditor() {
  const [project, setProject] = useState<MusicVideoProject>({
    title: 'Untitled Music Video',
    audioUrl: null,
    audioDuration: 60,
    lyrics: [],
    scenes: [],
    musicPrompt: '',
    lyricsText: '',
  });

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [activeSceneIdx, setActiveSceneIdx] = useState(0);
  const [generating, setGenerating] = useState<'music' | 'image' | null>(null);
  const [genSceneIdx, setGenSceneIdx] = useState(-1);
  const [statusMsg, setStatusMsg] = useState('');

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const animFrameRef = useRef<number>(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Audio playback loop ──
  const tick = useCallback(() => {
    if (audioRef.current && !audioRef.current.paused) {
      setCurrentTime(audioRef.current.currentTime);
      animFrameRef.current = requestAnimationFrame(tick);
    }
  }, []);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !project.audioUrl) return;
    if (audio.paused) {
      audio.play();
      setIsPlaying(true);
      animFrameRef.current = requestAnimationFrame(tick);
    } else {
      audio.pause();
      setIsPlaying(false);
      cancelAnimationFrame(animFrameRef.current);
    }
  }, [project.audioUrl, tick]);

  const restart = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      setCurrentTime(0);
    }
  }, []);

  // Determine which scene is active based on current time
  useEffect(() => {
    const { scenes } = project;
    for (let i = scenes.length - 1; i >= 0; i--) {
      if (currentTime >= scenes[i].startTime) {
        setActiveSceneIdx(i);
        return;
      }
    }
    setActiveSceneIdx(0);
  }, [currentTime, project.scenes]);

  // Active lyric line
  const activeLyric = (() => {
    const { lyrics } = project;
    for (let i = lyrics.length - 1; i >= 0; i--) {
      if (currentTime >= lyrics[i].time) return lyrics[i];
    }
    return null;
  })();

  // ── Audio load handler ──
  const onAudioLoaded = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const dur = audio.duration || 60;
    setProject((p) => {
      // Auto-generate default scenes if none exist
      if (p.scenes.length === 0) {
        const count = Math.max(1, Math.ceil(dur / 10));
        const scenes: Scene[] = Array.from({ length: count }, (_, i) => ({
          id: uid(),
          imageUrl: null,
          prompt: `Scene ${i + 1}`,
          startTime: i * (dur / count),
          duration: dur / count,
          kenBurns: KEN_BURNS_OPTIONS[i % 4].value,
        }));
        return { ...p, audioDuration: dur, scenes };
      }
      return { ...p, audioDuration: dur };
    });
  }, []);

  // ── Generate music via API ──
  const handleGenerateMusic = useCallback(async () => {
    if (!project.musicPrompt.trim()) return;
    setGenerating('music');
    setStatusMsg('Generating music...');
    try {
      const result = await generateMusic({
        prompt: project.musicPrompt,
        lyrics: project.lyricsText || undefined,
      });
      setProject((p) => ({ ...p, audioUrl: result.audioUrl }));
      setStatusMsg('Music generated!');
    } catch (err: any) {
      setStatusMsg(`Error: ${err.message}`);
    } finally {
      setGenerating(null);
    }
  }, [project.musicPrompt, project.lyricsText]);

  // ── Upload MP3 ──
  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setGenerating('music');
    setStatusMsg('Uploading audio...');
    try {
      const reader = new FileReader();
      const dataUrl = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const result = await uploadAudio({ data: dataUrl, fileName: file.name });
      setProject((p) => ({ ...p, audioUrl: result.audioUrl }));
      setStatusMsg('Audio uploaded!');
    } catch (err: any) {
      setStatusMsg(`Upload error: ${err.message}`);
    } finally {
      setGenerating(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, []);

  // ── Generate scene image ──
  const handleGenerateSceneImage = useCallback(async (sceneIdx: number) => {
    const scene = project.scenes[sceneIdx];
    if (!scene) return;
    setGenSceneIdx(sceneIdx);
    setGenerating('image');
    setStatusMsg(`Generating image for Scene ${sceneIdx + 1}...`);
    try {
      const result = await generateSceneImage({
        prompt: scene.prompt,
        width: 1280,
        height: 720,
      });
      setProject((p) => {
        const scenes = [...p.scenes];
        scenes[sceneIdx] = { ...scenes[sceneIdx], imageUrl: result.imageUrl };
        return { ...p, scenes };
      });
      setStatusMsg(`Scene ${sceneIdx + 1} image generated!`);
    } catch (err: any) {
      setStatusMsg(`Error: ${err.message}`);
    } finally {
      setGenerating(null);
      setGenSceneIdx(-1);
    }
  }, [project.scenes]);

  // ── Scene CRUD ──
  const addScene = useCallback(() => {
    setProject((p) => {
      const last = p.scenes[p.scenes.length - 1];
      const start = last ? last.startTime + last.duration : 0;
      const dur = Math.max(5, p.audioDuration - start);
      return {
        ...p,
        scenes: [
          ...p.scenes,
          {
            id: uid(),
            imageUrl: null,
            prompt: `Scene ${p.scenes.length + 1}`,
            startTime: start,
            duration: dur,
            kenBurns: KEN_BURNS_OPTIONS[p.scenes.length % 4].value,
          },
        ],
      };
    });
  }, []);

  const removeScene = useCallback((idx: number) => {
    setProject((p) => {
      const scenes = p.scenes.filter((_, i) => i !== idx);
      return { ...p, scenes };
    });
  }, []);

  const updateScene = useCallback((idx: number, patch: Partial<Scene>) => {
    setProject((p) => {
      const scenes = [...p.scenes];
      scenes[idx] = { ...scenes[idx], ...patch };
      return { ...p, scenes };
    });
  }, []);

  // ── Lyrics ──
  const updateLyricText = useCallback((id: string, text: string) => {
    setProject((p) => ({
      ...p,
      lyrics: p.lyrics.map((l) => (l.id === id ? { ...l, text } : l)),
    }));
  }, []);

  const removeLyric = useCallback((id: string) => {
    setProject((p) => ({
      ...p,
      lyrics: p.lyrics.filter((l) => l.id !== id),
    }));
  }, []);

  const addLyricLine = useCallback(() => {
    setProject((p) => {
      const lastTime = p.lyrics.length > 0 ? p.lyrics[p.lyrics.length - 1].time + 3 : 0;
      return {
        ...p,
        lyrics: [...p.lyrics, { id: uid(), time: lastTime, text: '' }],
      };
    });
  }, []);

  const applyLyricsFromText = useCallback(() => {
    setProject((p) => ({
      ...p,
      lyrics: parseLyrics(p.lyricsText, p.audioDuration),
    }));
  }, []);

  // ── Active scene for preview ──
  const activeScene = project.scenes[activeSceneIdx] || null;

  // Timeline pixel scale
  const PX_PER_SEC = 8;
  const timelineWidth = project.audioDuration * PX_PER_SEC;

  return (
    <div className={css.page}>
      {/* Hidden audio element */}
      {project.audioUrl && (
        <audio
          ref={audioRef}
          src={project.audioUrl}
          onLoadedMetadata={onAudioLoaded}
          onEnded={() => {
            setIsPlaying(false);
            cancelAnimationFrame(animFrameRef.current);
          }}
        />
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="audio/*"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {/* ── Header ── */}
      <header className={css.header}>
        <div className={css.headerLeft}>
          <a href="/dashboard" style={{ color: 'inherit', textDecoration: 'none' }}>
            <ChevronLeft size={20} />
          </a>
          <h1>
            <input
              value={project.title}
              onChange={(e) => setProject((p) => ({ ...p, title: e.target.value }))}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'inherit',
                font: 'inherit',
                width: '300px',
              }}
            />
          </h1>
        </div>
        <div className={css.headerRight}>
          <button className={css.uploadBtn} disabled>
            <Download size={14} />
            Export
          </button>
        </div>
      </header>

      {/* ── Body ── */}
      <div className={css.body}>
        {/* ── Sidebar ── */}
        <aside className={css.sidebar}>
          {/* Audio Section */}
          <div className={css.sidebarSection}>
            <h3>
              <IoIosMusicalNotes style={{ marginRight: 6, verticalAlign: 'middle' }} />
              Audio
            </h3>
            {project.audioUrl ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <IoIosMusicalNotes size={20} color="#ffd700" />
                  <span style={{ fontSize: '0.85rem' }}>
                    Track loaded ({fmtTime(project.audioDuration)})
                  </span>
                </div>
                <div className={css.audioControls}>
                  <button className={css.uploadBtn} onClick={handleUploadClick} disabled={generating === 'music'}>
                    <Upload size={14} />
                    Replace
                  </button>
                  <button
                    className={css.generateBtn}
                    onClick={handleGenerateMusic}
                    disabled={generating === 'music' || !project.musicPrompt.trim()}
                  >
                    <RefreshCw size={14} className={generating === 'music' ? css.spinner : ''} />
                    Regenerate
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div className={css.field}>
                  <label>Music Prompt</label>
                  <textarea
                    placeholder="Describe the music: genre, mood, tempo, instruments..."
                    value={project.musicPrompt}
                    onChange={(e) => setProject((p) => ({ ...p, musicPrompt: e.target.value }))}
                  />
                </div>
                <div className={css.audioControls}>
                  <button
                    className={css.generateBtn}
                    onClick={handleGenerateMusic}
                    disabled={generating === 'music' || !project.musicPrompt.trim()}
                  >
                    {generating === 'music' ? (
                      <RefreshCw size={14} className={css.spinner} />
                    ) : (
                      <Wand2 size={14} />
                    )}
                    Generate
                  </button>
                  <button className={css.uploadBtn} onClick={handleUploadClick} disabled={generating === 'music'}>
                    <Upload size={14} />
                    Upload MP3
                  </button>
                </div>
              </div>
            )}
            {generating === 'music' && (
              <div className={css.progressBar}>
                <div className={css.progressFill} style={{ width: '60%' }} />
              </div>
            )}
            {statusMsg && <div className={css.statusText}>{statusMsg}</div>}
          </div>

          {/* Lyrics Section */}
          <div className={css.sidebarSection}>
            <h3>Lyrics</h3>
            <div className={css.field}>
              <label>Paste lyrics (one line per verse)</label>
              <textarea
                placeholder="Line 1&#10;Line 2&#10;Line 3..."
                value={project.lyricsText}
                onChange={(e) => setProject((p) => ({ ...p, lyricsText: e.target.value }))}
              />
            </div>
            <button className={css.uploadBtn} onClick={applyLyricsFromText}>
              Apply to Timeline
            </button>
            {project.lyrics.length > 0 && (
              <div className={css.lyricsList} style={{ marginTop: 12 }}>
                {project.lyrics.map((line) => (
                  <div key={line.id} className={css.lyricRow}>
                    <span className={css.lyricTime}>{fmtTime(line.time)}</span>
                    <input
                      className={css.lyricInput}
                      value={line.text}
                      onChange={(e) => updateLyricText(line.id, e.target.value)}
                    />
                    <button
                      className={css.lyricDelete}
                      onClick={() => removeLyric(line.id)}
                      style={{ background: 'none', border: 'none', color: 'var(--muted-foreground)', cursor: 'pointer' }}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
                <button
                  className={css.uploadBtn}
                  onClick={addLyricLine}
                  style={{ marginTop: 4, width: 'fit-content' }}
                >
                  <Plus size={12} />
                  Add Line
                </button>
              </div>
            )}
          </div>

          {/* Scenes Section */}
          <div className={css.sidebarSection}>
            <h3>Scenes</h3>
            <div className={css.scenesGrid}>
              {project.scenes.map((scene, i) => (
                <div
                  key={scene.id}
                  className={`${css.sceneCard} ${i === activeSceneIdx ? css.sceneCardActive : ''}`}
                  onClick={() => setActiveSceneIdx(i)}
                >
                  {scene.imageUrl ? (
                    <img className={css.sceneCardImg} src={scene.imageUrl} alt={scene.prompt} />
                  ) : (
                    <div className={css.sceneCardPlaceholder}>
                      {genSceneIdx === i && generating === 'image' ? (
                        <RefreshCw size={16} className={css.spinner} />
                      ) : (
                        <ImagePlus size={16} />
                      )}
                    </div>
                  )}
                  <div className={css.sceneCardOverlay}>S{i + 1}</div>
                </div>
              ))}
            </div>
            <button
              className={css.uploadBtn}
              onClick={addScene}
              style={{ marginTop: 8, width: '100%', justifyContent: 'center' }}
            >
              <Plus size={14} />
              Add Scene
            </button>
          </div>

          {/* Active Scene Editor */}
          {activeScene && (
            <div className={css.sidebarSection}>
              <h3>Scene {activeSceneIdx + 1} Details</h3>
              <div className={css.field}>
                <label>Image Prompt</label>
                <textarea
                  value={activeScene.prompt}
                  onChange={(e) => updateScene(activeSceneIdx, { prompt: e.target.value })}
                  style={{ minHeight: 60 }}
                />
              </div>
              <div className={css.field}>
                <label>Ken Burns Effect</label>
                <select
                  value={activeScene.kenBurns}
                  onChange={(e) =>
                    updateScene(activeSceneIdx, { kenBurns: e.target.value as Scene['kenBurns'] })
                  }
                >
                  {KEN_BURNS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className={css.field}>
                <label>Duration (sec)</label>
                <input
                  type="number"
                  min={1}
                  value={Math.round(activeScene.duration)}
                  onChange={(e) =>
                    updateScene(activeSceneIdx, { duration: Math.max(1, Number(e.target.value)) })
                  }
                />
              </div>
              <div className={css.audioControls}>
                <button
                  className={css.generateBtn}
                  disabled={generating === 'image' || !activeScene.prompt.trim()}
                  onClick={() => handleGenerateSceneImage(activeSceneIdx)}
                >
                  {generating === 'image' && genSceneIdx === activeSceneIdx ? (
                    <RefreshCw size={14} className={css.spinner} />
                  ) : (
                    <Wand2 size={14} />
                  )}
                  {activeScene.imageUrl ? 'Regenerate Image' : 'Generate Image'}
                </button>
                <button
                  className={css.uploadBtn}
                  style={{ background: 'var(--destructive, #e74c3c)', color: '#fff' }}
                  onClick={() => removeScene(activeSceneIdx)}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          )}
        </aside>

        {/* ── Center: Preview ── */}
        <div className={css.center}>
          <div className={css.previewArea}>
            {activeScene ? (
              <div className={css.previewCanvas}>
                {activeScene.imageUrl ? (
                  <img
                    key={`${activeScene.id}-${activeScene.kenBurns}`}
                    className={`${css.kenBurnsImage} ${isPlaying ? KEN_BURNS_CLASS[activeScene.kenBurns] || '' : ''}`}
                    src={activeScene.imageUrl}
                    alt={activeScene.prompt}
                  />
                ) : (
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: SCENE_COLORS[activeSceneIdx % SCENE_COLORS.length] + '33',
                      color: '#fff',
                      fontSize: '1.2rem',
                    }}
                  >
                    Scene {activeSceneIdx + 1} — No image yet
                  </div>
                )}

                {/* Lyrics overlay */}
                {activeLyric && activeLyric.text && (
                  <div className={css.lyricsOverlay}>
                    <span className={css.lyricText}>{activeLyric.text}</span>
                  </div>
                )}

                {/* Play controls */}
                <div className={css.playControls}>
                  <button className={css.playBtn} onClick={restart}>
                    <SkipBack size={16} />
                  </button>
                  <button className={css.playBtn} onClick={togglePlay} disabled={!project.audioUrl}>
                    {isPlaying ? <Pause size={16} /> : <Play size={16} />}
                  </button>
                  <span className={css.timeDisplay}>
                    {fmtTime(currentTime)} / {fmtTime(project.audioDuration)}
                  </span>
                </div>
              </div>
            ) : (
              <div className={css.emptyState}>
                <IoIosMusicalNotes size={48} />
                <h3>Music Video Editor</h3>
                <p>Generate or upload audio, add scenes with AI images, and create your music video.</p>
              </div>
            )}
          </div>

          {/* ── Timeline ── */}
          <div className={css.timeline}>
            <div className={css.timelineHeader}>
              <span>Timeline</span>
              <span>{fmtTime(currentTime)} / {fmtTime(project.audioDuration)}</span>
            </div>
            <div className={css.timelineTracks}>
              {/* Scenes track */}
              <div className={css.timelineTrack}>
                <span className={css.trackLabel}>Scenes</span>
                <div className={css.trackContent} style={{ minWidth: timelineWidth }}>
                  {project.scenes.map((scene, i) => (
                    <div
                      key={scene.id}
                      className={css.sceneBlock}
                      style={{
                        left: scene.startTime * PX_PER_SEC,
                        width: Math.max(20, scene.duration * PX_PER_SEC),
                        background: SCENE_COLORS[i % SCENE_COLORS.length],
                      }}
                      onClick={() => setActiveSceneIdx(i)}
                    >
                      {scene.imageUrl && (
                        <img className={css.sceneBlockImg} src={scene.imageUrl} alt="" />
                      )}
                      <span className={css.sceneBlockLabel}>S{i + 1}</span>
                    </div>
                  ))}
                  {/* Playhead */}
                  <div className={css.playhead} style={{ left: currentTime * PX_PER_SEC }}>
                    <div className={css.playheadKnob} />
                  </div>
                </div>
              </div>

              {/* Lyrics track */}
              <div className={css.timelineTrack}>
                <span className={css.trackLabel}>Lyrics</span>
                <div className={css.trackContent} style={{ minWidth: timelineWidth }}>
                  {project.lyrics.map((line, i) => {
                    const nextTime =
                      i < project.lyrics.length - 1
                        ? project.lyrics[i + 1].time
                        : project.audioDuration;
                    const dur = nextTime - line.time;
                    return (
                      <div
                        key={line.id}
                        className={css.lyricBlock}
                        style={{
                          left: line.time * PX_PER_SEC,
                          width: Math.max(20, dur * PX_PER_SEC),
                        }}
                        title={line.text}
                      >
                        {line.text}
                      </div>
                    );
                  })}
                  {/* Playhead */}
                  <div className={css.playhead} style={{ left: currentTime * PX_PER_SEC }}>
                    <div className={css.playheadKnob} />
                  </div>
                </div>
              </div>

              {/* Audio track */}
              <div className={css.timelineTrack}>
                <span className={css.trackLabel}>Audio</span>
                <div className={css.trackContent} style={{ minWidth: timelineWidth }}>
                  {project.audioUrl && (
                    <div
                      className={css.sceneBlock}
                      style={{
                        left: 0,
                        width: project.audioDuration * PX_PER_SEC,
                        background: 'linear-gradient(135deg, #ffd700, #ff8c00)',
                      }}
                    >
                      <IoIosMusicalNotes size={14} style={{ position: 'relative', zIndex: 1 }} />
                      <span className={css.sceneBlockLabel} style={{ marginLeft: 4 }}>Audio</span>
                    </div>
                  )}
                  {/* Playhead */}
                  <div className={css.playhead} style={{ left: currentTime * PX_PER_SEC }}>
                    <div className={css.playheadKnob} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
